'use client';

import {
  useRef,
  useMemo,
  useEffect,
  useCallback,
  useImperativeHandle
} from 'react';

import CSS from './Slider.module.scss';
import CSSUtility from '@/styles/utility.module.scss';
import Vector from './elements/vector/Vector';

const SCROLL_DELTA = 2; // pixel

export interface SliderHandlers {
  onMouseDown: (event: React.MouseEvent) => void;
  onMouseMove: (event: React.MouseEvent) => void;
  onMouseUp: (event: React.MouseEvent) => void;
  onClick: (
    event: React.MouseEvent,
    callback?: (event?: React.MouseEvent) => void
  ) => void;
}

export default function Slider({
  ref,
  children,
  className,
  wrapperClassName,
  showControls,
  ...rest
}: {
  children: React.ReactNode;
  ref: React.ForwardedRef<SliderHandlers>;
  className?: HTMLDivElement['className'];
  wrapperClassName?: HTMLDivElement['className'];
  showControls?: boolean;
  rest?: React.HTMLAttributes<HTMLDivElement>;
}) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const swipeX = useRef<number | null>(null);
  const scrollLeft = useRef<HTMLElement['scrollLeft'] | null>(null);
  const cursor = useRef<HTMLElement['style']['cursor'] | null>(null);
  const allowClick = useRef<boolean>(false);

  /** Handles control buttons visibility and slider navigation. */
  const handleControls = useCallback(
    (isBack?: boolean) => {
      if (!showControls) return;

      const isDirect = isBack !== undefined;
      const slider = sliderRef.current!;
      const back = controlsRef.current!.querySelector(`.${CSS.back}`)!;
      const forward = controlsRef.current!.querySelector(`.${CSS.forward}`)!;
      const currentScrollLeft = slider.scrollLeft;
      const currentScrollLeftAbsolute = Math.abs(currentScrollLeft);
      const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

      back.setAttribute(
        'data-visible',
        String(
          isDirect
            ? !isBack
            : currentScrollLeft <= 0 &&
                currentScrollLeftAbsolute >= SCROLL_DELTA
        )
      );
      forward.setAttribute(
        'data-visible',
        String(
          isDirect
            ? isBack
            : maxScrollLeft - currentScrollLeftAbsolute >= SCROLL_DELTA
        )
      );
    },
    [showControls]
  );

  const onSwipe = useCallback((event: React.MouseEvent) => {
    const isChild = event.target !== sliderRef.current;
    if (isChild) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (event.type === 'mousedown') {
      allowClick.current = false;
      swipeX.current = event.clientX;
      scrollLeft.current = sliderRef.current!.scrollLeft;
      cursor.current = sliderRef.current!.style.cursor;
      sliderRef.current!.style.cursor = 'grab';
      sliderRef.current!.childNodes.forEach(
        child => ((child as HTMLElement).style.cursor = 'grab')
      );
    } else if (
      event.type === 'mousemove' &&
      swipeX.current !== null &&
      scrollLeft.current !== null
    ) {
      sliderRef.current!.scrollLeft =
        scrollLeft.current + (swipeX.current - event.clientX);
    } else if (event.type === 'mouseup') {
      if (
        isChild &&
        swipeX.current === event.clientX // no mouse movement
      ) {
        allowClick.current = true;
      }

      swipeX.current = null;
      scrollLeft.current = sliderRef.current!.scrollLeft;
      sliderRef.current!.style.cursor = cursor.current!;
      sliderRef.current!.childNodes.forEach(
        child => ((child as HTMLElement).style.cursor = cursor.current!)
      );
    }
  }, []);

  /**
   * Handles the clicks.
   * For correct slider behavior, slider's children should
   * use this event handler and pass the actual event handler
   * as callback instead of directly setting the event handler
   * on the element.
   */
  const onClick = useCallback(
    (
      event: React.MouseEvent,
      callback?: (event?: React.MouseEvent) => void
    ) => {
      if (!allowClick.current) {
        event.preventDefault();
        return;
      } else if (callback) {
        callback(event);
      }
    },
    []
  );

  const onBack = useCallback(() => {
    handleControls(true);
    sliderRef.current!.scrollTo({left: 0, behavior: 'smooth'});
  }, [handleControls]);

  const onForward = useCallback(() => {
    handleControls(false);
    sliderRef.current!.scrollTo({
      behavior: 'smooth',
      left: sliderRef.current!.clientWidth - sliderRef.current!.scrollWidth
    });
  }, [handleControls]);

  const handlers = useMemo(
    () => ({
      onMouseDown: onSwipe,
      onMouseMove: onSwipe,
      onMouseUp: onSwipe
    }),
    [onSwipe]
  );

  useImperativeHandle(
    ref,
    () => ({
      ...handlers,
      onClick
    }),
    [handlers, onClick]
  );

  useEffect(handleControls, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={[CSS.slider, wrapperClassName || ''].join(' ')}>
      <div
        {...rest}
        ref={sliderRef}
        className={[CSSUtility.scrollbarHidden, className || ''].join(' ')}
        onMouseLeave={() => (swipeX.current = null)}
        /**
         * @todo Replace with `onScrollEnd` event when available on Safari.
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollend_event
         */
        onScroll={() => handleControls()}
        {...handlers}>
        {children}
      </div>

      {showControls && (
        <div ref={controlsRef} className={CSS.controls}>
          <Vector
            name="IoIosArrowBack"
            className={CSS.back}
            data-visible={false}
            onClick={onBack}
            hasFocus
          />
          <Vector
            name="IoIosArrowBack"
            className={CSS.forward}
            data-visible={false}
            onClick={onForward}
            hasFocus
          />
        </div>
      )}
    </div>
  );
}
